import React, { useState } from 'react';
import SoundAvatar from '../../Common/SoundAvatar.jsx';
import useClipLookup from '../../Common/useClipLookup.js';
import GrandReveal from './GrandReveal.jsx';
import WinningSoundsReveal from '../../Common/WinningSoundsReveal.jsx';
import '../../../styles/theme.css';

export default function DriftHost({ room = {}, audioEngine = null, onForceAdvance }) {
  const lookupClip = useClipLookup();
  const modeState = room.modeState || {};
  const currentPhase = room.currentPhase || modeState.currentPhase || 'step_0';
  const players = Object.values(room.players || {}).filter(p => !p.isHost);
  const activePlayers = players.filter(p => p.connected !== false);
  const totalActive = activePlayers.length || 1;

  const [currentRevealChainIndex, setCurrentRevealChainIndex] = useState(0);

  const chains = modeState.chains || [];
  const submissions = modeState.submissions || {};
  const submittedCount = Object.keys(submissions).length;

  const isStepPhase = currentPhase.startsWith('step_') || currentPhase === 'answering';
  const isGrandReveal = currentPhase === 'grand_reveal';
  const isVoting = currentPhase === 'voting';
  const isResults = currentPhase === 'results' || currentPhase === 'game_over';

  const entryIndex = modeState.entryIndex || 0;
  const currentStepType = entryIndex % 2 === 0 ? 'text' : 'audio';

  return (
    <div
      data-testid="drift-host"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      {/* Step Building View */}
      {isStepPhase && (
        <div
          style={{
            padding: '36px 32px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
            <div>
              <div style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--sd-teal, #3ACFCF)',
                fontWeight: 600,
              }}>
                DRIFT MODE — STEP {entryIndex + 1} OF {totalActive}
              </div>
              <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', margin: '8px 0 0 0' }}>
                {currentStepType === 'text' ? '✍️ Writing Text Phrases' : '🔊 Choosing Sound Clips'}
              </h2>
            </div>

            <button
              data-testid="force-advance-btn"
              onClick={onForceAdvance}
              style={{
                padding: '0 20px',
                minHeight: '48px',
                borderRadius: '12px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line-2, #1C2229)',
                color: 'var(--sd-amber, #E0A458)',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              }}
            >
              Force Advance ⏩
            </button>
          </div>

          <div style={{
            background: 'var(--sd-panel-2, #0E1116)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px',
            border: '1px solid var(--sd-line-2, #1C2229)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '1.1rem', fontWeight: 600 }}>
              <span style={{ color: 'var(--sd-muted, #79838D)' }}>Submissions Received</span>
              <span style={{ color: 'var(--sd-teal, #3ACFCF)', fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace" }}>
                {submittedCount} / {totalActive}
              </span>
            </div>

            <div style={{ width: '100%', height: '10px', background: 'var(--sd-ground, #08090B)', borderRadius: '5px', overflow: 'hidden' }}>
              <div
                style={{
                  width: `${(submittedCount / totalActive) * 100}%`,
                  height: '100%',
                  background: 'var(--sd-teal, #3ACFCF)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
            {players.map((p) => {
              const hasSubmitted = Boolean(submissions[p.id]);
              return (
                <div
                  key={p.id}
                  style={{
                    background: hasSubmitted ? 'rgba(143, 199, 160, 0.10)' : 'var(--sd-panel-2, #0E1116)',
                    border: `1px solid ${hasSubmitted ? 'rgba(143, 199, 160, 0.35)' : 'var(--sd-line-2, #1C2229)'}`,
                    borderRadius: '16px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                  }}
                >
                  <SoundAvatar clip={lookupClip(p.avatar)} seed={p.name} size="md" />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)', fontSize: '1rem' }}>
                      {p.name}
                    </div>
                    <div style={{
                      fontSize: '0.85rem',
                      color: hasSubmitted ? 'var(--sd-sage, #8FC7A0)' : 'var(--sd-dim, #6E7982)',
                      fontWeight: 600,
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    }}>
                      {hasSubmitted ? '✓ Submitted' : 'Thinking...'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Grand Reveal View */}
      {isGrandReveal && (
        <GrandReveal
          chains={chains}
          currentChainIndex={currentRevealChainIndex}
          audioEngine={audioEngine}
          onNextChain={() => setCurrentRevealChainIndex(prev => Math.min(prev + 1, chains.length - 1))}
          onPrevChain={() => setCurrentRevealChainIndex(prev => Math.max(prev - 1, 0))}
          onFinishReveal={onForceAdvance}
        />
      )}

      {/* Voting View */}
      {isVoting && (
        <div
          data-testid="drift-host-voting"
          style={{
            padding: '36px 32px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div>
              <div style={{
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                fontSize: '12px',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--sd-amber, #E0A458)',
                fontWeight: 600,
              }}>
                VOTING PHASE
              </div>
              <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', margin: '8px 0 0 0' }}>
                Vote on Phone Screens!
              </h2>
              <p style={{ color: 'var(--sd-muted, #79838D)', fontSize: '1.1rem', marginTop: '6px' }}>
                Pick the funniest drift moment & most accurate guess.
              </p>
            </div>

            <button
              data-testid="force-advance-btn"
              onClick={onForceAdvance}
              style={{
                padding: '0 20px',
                minHeight: '48px',
                borderRadius: '12px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line-2, #1C2229)',
                color: 'var(--sd-amber, #E0A458)',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              }}
            >
              Force End Voting ⏩
            </button>
          </div>

          <div style={{
            background: 'var(--sd-panel-2, #0E1116)',
            borderRadius: '16px',
            padding: '24px',
            border: '1px solid var(--sd-line-2, #1C2229)'
          }}>
            <div style={{
              fontSize: '1.3rem',
              fontWeight: 700,
              color: 'var(--sd-teal, #3ACFCF)',
              marginBottom: '8px',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            }}>
              Votes Cast: {submittedCount} / {totalActive}
            </div>
            <p style={{ color: 'var(--sd-muted, #79838D)', margin: 0 }}>
              Look at your phone to vote for your favorite telephone drift moments!
            </p>
          </div>
        </div>
      )}

      {/* Results View */}
      {isResults && (
        <div
          data-testid="drift-host-results"
          style={{
            padding: '36px 32px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '12px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-sage, #8FC7A0)',
            fontWeight: 600,
          }}>
            DRIFT CHAMPIONS
          </div>
          <h2 style={{ fontSize: '2.4rem', fontWeight: 800, color: 'var(--sd-ink, #EDEFF2)', margin: '8px 0 24px 0' }}>
            Final Leaderboard & Awards
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {players
              .slice()
              .sort((a, b) => (b.score || 0) - (a.score || 0))
              .map((p, idx) => (
                <div
                  key={p.id}
                  style={{
                    background: idx === 0 ? 'rgba(224, 164, 88, 0.12)' : 'var(--sd-panel-2, #0E1116)',
                    border: `1px solid ${idx === 0 ? 'var(--sd-amber, #E0A458)' : 'var(--sd-line-2, #1C2229)'}`,
                    borderRadius: '16px',
                    padding: '18px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      fontSize: '1.4rem',
                      fontWeight: 800,
                      color: idx === 0 ? 'var(--sd-amber, #E0A458)' : 'var(--sd-dim, #6E7982)',
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    }}>
                      #{idx + 1}
                    </div>
                    <SoundAvatar clip={lookupClip(p.avatar)} seed={p.name} size="md" />
                    <div style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)' }}>
                      {p.name}
                    </div>
                  </div>

                  <div style={{
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    color: 'var(--sd-teal, #3ACFCF)',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                  }}>
                    {p.score || 0} pts
                  </div>
                </div>
              ))}
          </div>

          <WinningSoundsReveal room={room} audioEngine={audioEngine} />
        </div>
      )}
    </div>
  );
}
